import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { pushSupported, enablePush, disablePush, pushStatus, sendTestPush } from '@/lib/push';

/**
 * EnablePushButton — one-tap toggle to enable/disable Web Push.
 * Shown in the Investor Portal (and anywhere else we want).
 */
export const EnablePushButton = ({ variant = 'inline', className = '' }) => {
  const [supported, setSupported] = useState(true);
  const [status, setStatus] = useState({ subscribed: false, permission: 'default' });
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    const s = await pushStatus();
    setStatus(s);
  };

  useEffect(() => {
    setSupported(pushSupported());
    if (pushSupported()) refresh();
  }, []);

  if (!supported) return null;

  const handleEnable = async () => {
    setLoading(true);
    try {
      await enablePush();
      toast.success('Push notifications enabled 🔔');
      await refresh();
      // Fire a confirmation push so the user sees it works
      try { await sendTestPush(); } catch (_) {}
    } catch (e) {
      toast.error(e?.message || 'Could not enable notifications.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await disablePush();
      toast.success('Push notifications disabled');
      await refresh();
    } catch (e) {
      toast.error('Failed to disable.');
    } finally {
      setLoading(false);
    }
  };

  const isOn = status.subscribed && status.permission === 'granted';
  const isBlocked = status.permission === 'denied';

  if (variant === 'card') {
    return (
      <div
        data-testid="push-notification-card"
        className={`rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4 flex items-center justify-between shadow-sm ${className}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isOn ? 'bg-emerald-500' : 'bg-emerald-100'}`}>
            {isOn ? <BellRing className="w-5 h-5 text-white" /> : <Bell className="w-5 h-5 text-emerald-700" />}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">
              {isOn ? 'Notifications ON' : 'Get Instant Alerts'}
            </div>
            <div className="text-xs text-slate-500 leading-tight">
              {isOn
                ? 'Deposits, withdrawals & trade wins — right on your phone'
                : isBlocked
                ? 'Blocked — enable in browser settings'
                : 'Deposits, withdrawals & trade wins'}
            </div>
          </div>
        </div>
        {isBlocked ? (
          <span className="text-xs font-semibold text-rose-600">Blocked</span>
        ) : isOn ? (
          <button
            data-testid="push-disable-btn"
            onClick={handleDisable}
            disabled={loading}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Turn Off
          </button>
        ) : (
          <button
            data-testid="push-enable-btn"
            onClick={handleEnable}
            disabled={loading}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
          >
            {loading ? 'Enabling…' : 'Enable'}
          </button>
        )}
      </div>
    );
  }

  // inline / pill variant
  return (
    <button
      data-testid="push-toggle-btn"
      onClick={isOn ? handleDisable : handleEnable}
      disabled={loading || isBlocked}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
        isOn
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : isBlocked
          ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
          : 'bg-emerald-600 text-white hover:bg-emerald-700'
      } ${className}`}
    >
      {isOn ? <BellRing className="w-3.5 h-3.5" /> : isBlocked ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
      {loading ? '…' : isOn ? 'Alerts On' : isBlocked ? 'Blocked' : 'Enable Alerts'}
    </button>
  );
};

export default EnablePushButton;
